import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    Banknote, ChevronRight, ChevronDown, RefreshCw, Download, Loader2, AlertTriangle, Filter, Search,
} from 'lucide-react';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchClientPayClients, fetchClientPaySubClients, fetchClientPayCCList, fetchClientPayPOInv, fetchClientPayReport,
    clearSubClients, clearCCList, clearPOInv, clearClientPayReport, resetClientPaymentsReport,
    selectCPRClientList, selectCPRClientListLoading,
    selectCPRSubClientList, selectCPRSubClientListLoading,
    selectCPRCCList, selectCPRCCListLoading,
    selectCPRPOList, selectCPRInvList, selectCPRPOInvLoading,
    selectCPRReport, selectCPRReportLoading, selectCPRReportError,
} from '../../slices/ReportsSlice/clientPaymentsReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const REPORT_TYPES = [
    { Type: 'Advance', Desc: 'Advance' },
    { Type: 'Retention', Desc: 'Retention' },
    { Type: 'Hold', Desc: 'Hold' },
];
const SELECT_ALL = 'SelectAll';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Several of this report's lookup SPs embed the code in the display column
// (spGetClientPayclients' ClientName = "code,name", spGetClientPaySubClient's
// SubClientCodename = "code ,address", spGetClientPayCC's CC_Name = "code , name") —
// strip the leading "code," prefix before rendering.
const stripLeadingCode = (raw) => {
    const idx = (raw || '').indexOf(',');
    return idx === -1 ? raw : raw.slice(idx + 1).trim();
};

// ── Compact multi-select dropdown (mirrors the one built for Vendor Invoice Report) ──
const MultiSelectDropdown = ({ label, options, valueKey, textKey, selected, onChange, disabled, loading }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const allSelected = options.length > 0 && selected.length === options.length;
    const toggleAll = () => onChange(allSelected ? [] : options.map((o) => String(o[valueKey])));
    const toggleOne = (val) => onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);

    return (
        <div className="relative" ref={ref}>
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
            <button type="button" disabled={disabled || loading}
                onClick={() => setOpen((o) => !o)}
                className={`${inputCls} flex items-center justify-between text-left disabled:opacity-60 disabled:cursor-not-allowed`}>
                <span className="truncate">
                    {loading ? 'Loading…' : selected.length === 0 ? '-Please Select-' : `${selected.length} selected`}
                </span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            </button>
            {open && !disabled && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                    {options.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-400">No options</div>
                    ) : (
                        <>
                            <label className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                                <input type="checkbox" className="accent-indigo-600" checked={allSelected} onChange={toggleAll} />
                                Select All
                            </label>
                            {options.map((o) => (
                                <label key={o[valueKey]} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                                    <input type="checkbox" className="accent-indigo-600"
                                        checked={selected.includes(String(o[valueKey]))} onChange={() => toggleOne(String(o[valueKey]))} />
                                    <span className="truncate">{o[textKey]}</span>
                                </label>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// Column config per report type — drives both the table render and the Excel export.
const COLUMNS = {
    Advance: [
        { key: 'TransactionDate', header: 'Transaction Date' },
        { key: 'PONo', header: 'PO No' },
        { key: 'CCCode', header: 'CC' },
        { key: 'Client', header: 'Client' },
        { key: 'SubClient', header: 'Sub Client' },
        { key: 'AdvanceAmout', header: 'Advance', money: true },
        { key: 'AdvanceBalance', header: 'Balance', money: true },
    ],
    Retention: [
        { key: 'PONo', header: 'PO No' },
        { key: 'InvoiceNo', header: 'Invoice No' },
        { key: 'InvoiceDate', header: 'Invoice Date' },
        { key: 'TransactionDate', header: 'Transaction Date' },
        { key: 'CCCode', header: 'CC' },
        { key: 'PaidAmout', header: 'Paid Amount', money: true },
        { key: 'Retention', header: 'Retention', money: true },
        { key: 'RetentionBalance', header: 'Balance', money: true },
    ],
    Hold: [
        { key: 'PONo', header: 'PO No' },
        { key: 'InvoiceNo', header: 'Invoice No' },
        { key: 'InvoiceDate', header: 'Invoice Date' },
        { key: 'TransactionDate', header: 'Transaction Date' },
        { key: 'CCCode', header: 'CC' },
        { key: 'PaidAmout', header: 'Paid Amount', money: true },
        { key: 'Hold', header: 'Hold', money: true },
        { key: 'HoldBalance', header: 'Balance', money: true },
    ],
};

const ClientPaymentsReport = () => {
    const dispatch = useDispatch();

    const clientList = useSelector(selectCPRClientList);
    const clientListLoading = useSelector(selectCPRClientListLoading);
    const subClientListRaw = useSelector(selectCPRSubClientList);
    const subClientListLoading = useSelector(selectCPRSubClientListLoading);
    const ccListRaw = useSelector(selectCPRCCList);
    const ccListLoading = useSelector(selectCPRCCListLoading);
    const poList = useSelector(selectCPRPOList);
    const invList = useSelector(selectCPRInvList);
    const poInvLoading = useSelector(selectCPRPOInvLoading);
    const report = useSelector(selectCPRReport);
    const reportLoading = useSelector(selectCPRReportLoading);
    const reportError = useSelector(selectCPRReportError);

    const [reportType, setReportType] = useState('');
    const [clientCode, setClientCode] = useState('');
    const [subClientCode, setSubClientCode] = useState('');
    const [ccStatus, setCcStatus] = useState('');
    const [selectedCCs, setSelectedCCs] = useState([]);
    const [selectedPOs, setSelectedPOs] = useState([]);
    const [selectedInvs, setSelectedInvs] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [ranParams, setRanParams] = useState(null);

    const isAdvance = reportType === 'Advance';

    const clientOptions = useMemo(() => [
        { ClientCode: SELECT_ALL, ClientName: 'Select All' },
        ...clientList.map((c) => ({ ...c, ClientName: stripLeadingCode(c.ClientName) })),
    ], [clientList]);
    const subClientOptions = useMemo(() => [
        { SubClientCode: SELECT_ALL, SubClientCodename: 'Select All' },
        ...subClientListRaw.map((c) => ({ ...c, SubClientCodename: stripLeadingCode(c.SubClientCodename) })),
    ], [subClientListRaw]);
    const ccOptions = useMemo(() => ccListRaw.map((c) => ({ ...c, CC_Name: stripLeadingCode(c.CC_Name) })), [ccListRaw]);

    useEffect(() => () => dispatch(resetClientPaymentsReport()), [dispatch]);

    const handleReportTypeChange = (val) => {
        setReportType(val);
        setClientCode(''); setSubClientCode(''); setCcStatus('');
        setSelectedCCs([]); setSelectedPOs([]); setSelectedInvs([]);
        setFromDate(''); setToDate(''); setRanParams(null);
        dispatch(clearSubClients()); dispatch(clearCCList()); dispatch(clearPOInv()); dispatch(clearClientPayReport());
        if (val) dispatch(fetchClientPayClients(val));
    };

    const handleClientChange = (val) => {
        setClientCode(val);
        setSubClientCode(''); setCcStatus('');
        setSelectedCCs([]); setSelectedPOs([]); setSelectedInvs([]);
        dispatch(clearCCList()); dispatch(clearPOInv()); dispatch(clearClientPayReport());
        dispatch(clearSubClients());
        if (val) dispatch(fetchClientPaySubClients({ reportType, clientCode: val }));
    };

    const handleSubClientChange = (val) => {
        setSubClientCode(val);
        setCcStatus('');
        setSelectedCCs([]); setSelectedPOs([]); setSelectedInvs([]);
        dispatch(clearCCList()); dispatch(clearPOInv()); dispatch(clearClientPayReport());
    };

    const handleCCStatusChange = (val) => {
        setCcStatus(val);
        setSelectedCCs([]); setSelectedPOs([]); setSelectedInvs([]);
        dispatch(clearCCList()); dispatch(clearPOInv()); dispatch(clearClientPayReport());
        if (val) dispatch(fetchClientPayCCList({ reportType, clientCode, subClientCode, ccStatus: val }));
    };

    const handleCCsChange = (vals) => {
        setSelectedCCs(vals);
        setSelectedPOs([]); setSelectedInvs([]);
        dispatch(clearPOInv());
        if (vals.length > 0) {
            dispatch(fetchClientPayPOInv({ Reporttype: reportType, ClientCode: clientCode, SubClientCode: subClientCode, SelectedCC: vals }));
        }
    };

    const handleReset = () => {
        setReportType(''); setClientCode(''); setSubClientCode(''); setCcStatus('');
        setSelectedCCs([]); setSelectedPOs([]); setSelectedInvs([]);
        setFromDate(''); setToDate(''); setRanParams(null);
        dispatch(resetClientPaymentsReport());
    };

    const runReport = () => {
        if (!reportType) { toast.warning('Select Report Type'); return; }
        if (!clientCode) { toast.warning('Select Client'); return; }
        if (!subClientCode) { toast.warning('Select Sub Client'); return; }
        if (!ccStatus) { toast.warning(`Select Cost Centre Status`); return; }
        if (selectedCCs.length === 0) { toast.warning('Select Cost Centre'); return; }
        if (isAdvance && selectedPOs.length === 0) { toast.warning('Select PO Number'); return; }
        if (!isAdvance && selectedInvs.length === 0) { toast.warning('Select Invoice Number'); return; }

        // Legacy submits PO/Invoice numbers by their display text, not their id —
        // map the selected ids back to text before sending.
        const poTexts = poList.filter((p) => selectedPOs.includes(String(p.ClientPOId))).map((p) => p.PONumber);
        const invTexts = invList.filter((i) => selectedInvs.includes(String(i.Invoiceid))).map((i) => i.Invoiceno);

        const payload = {
            Reporttype: reportType,
            ClientCode: clientCode,
            SubClientCode: subClientCode,
            SelectedCC: selectedCCs,
            ...(isAdvance ? { SelectedPO: poTexts } : { SelectedInv: invTexts }),
            FromDate: toDDMonYYYY(fromDate),
            ToDate: toDDMonYYYY(toDate),
        };
        setRanParams({ reportType, payload });
        dispatch(fetchClientPayReport({ reportType, payload }));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchClientPayReport(ranParams)); };

    const columns = COLUMNS[reportType] || [];
    const totalsKeys = columns.filter((c) => c.money).map((c) => c.key);
    const totals = useMemo(() => report.reduce((acc, r) => {
        totalsKeys.forEach((k) => { acc[k] = (acc[k] || 0) + num(r[k]); });
        return acc;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, {}), [report, reportType]);

    const handleExport = () => {
        if (report.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = report.map((r) => {
            const row = {};
            columns.forEach((c) => { row[c.header] = r[c.key]; });
            return row;
        });
        const totalRow = {};
        columns.forEach((c) => { totalRow[c.header] = c.money ? totals[c.key] : (c === columns[0] ? 'TOTAL' : ''); });
        exportRows.push(totalRow);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), `Client ${reportType}`);
        XLSX.writeFile(wb, `Client_Payments_${reportType}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Banknote className="h-4 w-4 text-white" />
                            </div>
                            Client Payments Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Advance, Retention and Hold payments received against client invoices/POs</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Client Payments Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={report.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <Filter className="h-3.5 w-3.5 text-indigo-600" />
                        <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                    </div>
                    {ranParams && (
                        <button onClick={handleReset} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                            <RefreshCw className="w-3 h-3" /> Change Filters
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Report Type</label>
                        <select className={inputCls} value={reportType} onChange={(e) => handleReportTypeChange(e.target.value)} disabled={!!ranParams}>
                            <option value="">-Please Select-</option>
                            {REPORT_TYPES.map((t) => <option key={t.Type} value={t.Type}>{t.Desc}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Client</label>
                        <select className={inputCls} value={clientCode} onChange={(e) => handleClientChange(e.target.value)} disabled={!reportType || clientListLoading || !!ranParams}>
                            <option value="">-Please Select-</option>
                            {clientOptions.map((c) => <option key={c.ClientCode} value={c.ClientCode}>{c.ClientName}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Sub-Client</label>
                        <select className={inputCls} value={subClientCode} onChange={(e) => handleSubClientChange(e.target.value)} disabled={!clientCode || subClientListLoading || !!ranParams}>
                            <option value="">-Please Select-</option>
                            {subClientOptions.map((c) => <option key={c.SubClientCode} value={c.SubClientCode}>{c.SubClientCodename}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cost Centre Status</label>
                        <select className={inputCls} value={ccStatus} onChange={(e) => handleCCStatusChange(e.target.value)} disabled={!subClientCode || !!ranParams}>
                            <option value="">-Please Select-</option>
                            <option value="Active">Active</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mt-3">
                    <MultiSelectDropdown label="Cost Centre" options={ccOptions} valueKey="CC_Code" textKey="CC_Name"
                        selected={selectedCCs} onChange={handleCCsChange} disabled={!ccStatus || !!ranParams} loading={ccListLoading} />
                    {isAdvance ? (
                        <MultiSelectDropdown label="PO No" options={poList} valueKey="ClientPOId" textKey="PONumber"
                            selected={selectedPOs} onChange={setSelectedPOs} disabled={selectedCCs.length === 0 || !!ranParams} loading={poInvLoading} />
                    ) : (
                        <MultiSelectDropdown label="Invoice No" options={invList} valueKey="Invoiceid" textKey="Invoiceno"
                            selected={selectedInvs} onChange={setSelectedInvs} disabled={selectedCCs.length === 0 || !!ranParams} loading={poInvLoading} />
                    )}
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">From Date</label>
                        <CustomDatePicker value={fromDate} onChange={setFromDate} maxDate={new Date()} disabled={!!ranParams} />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">To Date</label>
                        <CustomDatePicker value={toDate} onChange={setToDate} minDate={fromDate || null} maxDate={new Date()} disabled={!!ranParams} />
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <button onClick={runReport} disabled={reportLoading || !!ranParams} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center gap-1.5">
                        <Search className="w-3.5 h-3.5" /> View
                    </button>
                    <button onClick={handleReset} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && report.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found.</div>
            ) : report.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">{reportType} Payments</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    {columns.map((c) => (
                                        <th key={c.key} className={`px-2 py-1.5 text-[10px] font-bold text-white uppercase whitespace-nowrap ${c.money ? 'text-right' : 'text-left'}`}>{c.header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {report.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        {columns.map((c) => (
                                            <td key={c.key} className={`px-2 py-1 text-xs whitespace-nowrap ${c.money ? 'text-right' : ''}`}>
                                                {c.money ? money(row[c.key]) : row[c.key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    {columns.map((c, i) => (
                                        <td key={c.key} className={`px-2 py-1 text-xs ${c.money ? 'text-right' : ''}`}>
                                            {c.money ? money(totals[c.key]) : (i === 0 ? 'TOTAL' : '')}
                                        </td>
                                    ))}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ClientPaymentsReport;
